// Cliente HTTP + hooks para el endpoint unificado del calendario (M2.34.C).
//
// Devuelve `workout-planned | workout-actual | meal | prep` en un solo
// payload. Los componentes existentes del calendario (`DayChip`, `DayView`,
// `MonthView`, `WeekView`) siguen consumiendo `useWorkoutsInRange` para
// workouts; este hook se usa SÓLO para enriquecer la vista con meals/preps
// cuando el toggle "Mostrar comidas" está activo.

import { z } from 'zod';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(
  /\/$/,
  '',
);

export const CalendarItemKindSchema = z.enum([
  'workout-planned',
  'workout-actual',
  'meal',
  'prep',
]);
export type CalendarApiItemKind = z.infer<typeof CalendarItemKindSchema>;

export const CalendarApiItemSchema = z.object({
  kind: CalendarItemKindSchema,
  id: z.string(),
  datetime: z.string(),
  durationMin: z.number().nonnegative(),
  title: z.string(),
  discipline: z.string().optional(),
  distanceM: z.number().nullable().optional(),
  tss: z.number().nullable().optional(),
  intensityZone: z.string().nullable().optional(),
  mealType: z.string().optional(),
  recipeId: z.number().nullable().optional(),
  mealPlanItemId: z.number().optional(),
  kcal: z.number().nullable().optional(),
  proteinG: z.number().nullable().optional(),
  carbsG: z.number().nullable().optional(),
  fatG: z.number().nullable().optional(),
  prepRecipeId: z.number().optional(),
});
export type CalendarApiItem = z.infer<typeof CalendarApiItemSchema>;

export const CalendarItemsResponseSchema = z.object({
  items: z.array(CalendarApiItemSchema),
});
export type CalendarItemsResponse = z.infer<typeof CalendarItemsResponseSchema>;

async function getCalendarItems(
  fromISO: string,
  toISO: string,
): Promise<CalendarItemsResponse> {
  const url = `${API_BASE_URL}/api/calendar/items?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`calendar items HTTP ${res.status}`);
  }
  const json: unknown = await res.json();
  return CalendarItemsResponseSchema.parse(json);
}

const CALENDAR_ITEMS_QUERY_KEY = ['calendar', 'items'] as const;

/**
 * Hook para `GET /api/calendar/items?from&to`. Cachea por rango como
 * `useWorkoutsInRange`. Útil para superponer meal/prep cards sobre la
 * timeline existente.
 *
 * Si `enabled` es false (toggle "Mostrar comidas" desactivado), la query
 * no se dispara — ahorramos un round-trip.
 */
export function useCalendarItems(
  fromISO: string,
  toISO: string,
  enabled = true,
): UseQueryResult<CalendarItemsResponse, Error> {
  return useQuery<CalendarItemsResponse, Error>({
    queryKey: [...CALENDAR_ITEMS_QUERY_KEY, fromISO, toISO],
    queryFn: () => getCalendarItems(fromISO, toISO),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}
